import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { PanelSpan } from '@/components/shared';
import { useDetailPanelInteractionValue } from '@/components/shared';
import { useBackofficePreference } from '@/preferences';
import { formatStatus } from '../format';
import type { BackofficeTranscriptMessage } from '../types';
import { InterviewDetailPanel } from './InterviewDetailPanelGrid';
import {
  INTERVIEW_RECORDING_INTERACTION_KEY,
  type InterviewRecordingInteractionState,
} from './interviewTileInteractions';
import {
  InterviewRecordingTimeShareButton,
  type RecordingTimeShareLocation,
} from './InterviewRecordingTimeShareButton';

export interface InterviewTranscriptPanelProps {
  transcript: BackofficeTranscriptMessage[];
  panelId?: string | undefined;
  currentSpan?: PanelSpan | undefined;
  preferenceScope?: string | undefined;
  shareLocation?: RecordingTimeShareLocation | undefined;
}

const DEFAULT_TRANSCRIPT_TIMESTAMP_ADJUSTMENT_MS = 0;

function parseTimestampAdjustmentMs(value: string): number {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : DEFAULT_TRANSCRIPT_TIMESTAMP_ADJUSTMENT_MS;
}

function adjustTranscriptOffsetSeconds(
  offsetSeconds: number | undefined,
  timestampAdjustmentMs: number
): number | undefined {
  if (offsetSeconds === undefined || !Number.isFinite(offsetSeconds)) {
    return undefined;
  }

  return Math.max(0, offsetSeconds + timestampAdjustmentMs / 1000);
}

function findActiveTranscriptIndex(
  transcript: BackofficeTranscriptMessage[],
  currentTimeSeconds: number,
  timestampAdjustmentMs: number
): number | null {
  if (!Number.isFinite(currentTimeSeconds) || currentTimeSeconds < 0) {
    return null;
  }

  for (let index = 0; index < transcript.length; index++) {
    const message = transcript[index]!;
    const offsetSeconds = adjustTranscriptOffsetSeconds(
      message.offsetSeconds,
      timestampAdjustmentMs
    );
    if (offsetSeconds === undefined) {
      continue;
    }

    const nextMessage = transcript
      .slice(index + 1)
      .find((candidate) => candidate.offsetSeconds !== undefined);
    const nextOffset = adjustTranscriptOffsetSeconds(
      nextMessage?.offsetSeconds,
      timestampAdjustmentMs
    );
    if (
      (currentTimeSeconds >= offsetSeconds || index === 0) &&
      (nextOffset === undefined || currentTimeSeconds < nextOffset)
    ) {
      return index;
    }
  }

  return null;
}

function formatTranscriptTime(timestamp: string, offsetSeconds: number | undefined): string {
  const timeOfDay = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(timestamp));

  if (offsetSeconds === undefined || !Number.isFinite(offsetSeconds) || offsetSeconds < 0) {
    return timeOfDay;
  }

  return `${timeOfDay} (+${formatRelativeAudioTime(offsetSeconds)})`;
}

function formatRelativeAudioTime(seconds: number): string {
  const totalCentiseconds = Math.round(seconds * 100);
  const totalSeconds = Math.floor(totalCentiseconds / 100);
  const centiseconds = totalCentiseconds % 100;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;
  const secondPart = `${remainingSeconds.toString().padStart(2, '0')}.${centiseconds
    .toString()
    .padStart(2, '0')}`;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secondPart}`;
  }

  return `${minutes.toString().padStart(2, '0')}:${secondPart}`;
}

function getTranscriptSeekTargetSeconds(
  offsetSeconds: number | undefined,
  index: number,
  timestampAdjustmentMs: number
): number | undefined {
  if (index === 0) {
    return 0;
  }

  return adjustTranscriptOffsetSeconds(offsetSeconds, timestampAdjustmentMs);
}

export function InterviewTranscriptPanel({
  transcript,
  panelId,
  currentSpan,
  preferenceScope,
  shareLocation,
}: InterviewTranscriptPanelProps) {
  const seekRequestIdRef = useRef(0);
  const defaultSettings = useMemo(
    () => ({
      timestampAdjustmentMs: DEFAULT_TRANSCRIPT_TIMESTAMP_ADJUSTMENT_MS,
      autoPlayOnClick: false,
    }),
    []
  );
  const preference = useBackofficePreference(preferenceScope, {
    defaultValue: defaultSettings,
    debounceMs: 500,
  });
  const [timestampAdjustmentInput, setTimestampAdjustmentInput] = useState(
    String(DEFAULT_TRANSCRIPT_TIMESTAMP_ADJUSTMENT_MS)
  );
  const [autoPlayOnClick, setAutoPlayOnClick] = useState(false);
  const timestampAdjustmentMs = parseTimestampAdjustmentMs(timestampAdjustmentInput);
  const [recordingInteraction, setRecordingInteraction] =
    useDetailPanelInteractionValue<InterviewRecordingInteractionState>(
      INTERVIEW_RECORDING_INTERACTION_KEY
    );
  const activeTranscriptIndex =
    recordingInteraction === undefined
      ? null
      : findActiveTranscriptIndex(
          transcript,
          recordingInteraction.currentTimeSeconds,
          timestampAdjustmentMs
        );
  const activeTranscriptRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!preference.loaded) {
      return;
    }

    setTimestampAdjustmentInput(String(preference.value.timestampAdjustmentMs));
    setAutoPlayOnClick(preference.value.autoPlayOnClick);
  }, [preference.loaded, preference.value.autoPlayOnClick, preference.value.timestampAdjustmentMs]);

  useEffect(() => {
    if (
      activeTranscriptIndex === null ||
      !recordingInteraction?.isPlaying ||
      !recordingInteraction.autoScrollTranscript
    ) {
      return;
    }

    if (typeof activeTranscriptRef.current?.scrollIntoView === 'function') {
      activeTranscriptRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [
    activeTranscriptIndex,
    recordingInteraction?.autoScrollTranscript,
    recordingInteraction?.isPlaying,
  ]);

  function seekToTranscriptOffset(rawOffsetSeconds: number | undefined, index: number) {
    const offsetSeconds = getTranscriptSeekTargetSeconds(
      rawOffsetSeconds,
      index,
      timestampAdjustmentMs
    );
    if (offsetSeconds === undefined) {
      return;
    }

    seekRequestIdRef.current += 1;
    const seekRequest = {
      id: seekRequestIdRef.current,
      timeSeconds: offsetSeconds,
      playAfterSeek: autoPlayOnClick,
    };

    setRecordingInteraction((current) => ({
      currentTimeSeconds: current?.currentTimeSeconds ?? offsetSeconds,
      durationSeconds: current?.durationSeconds ?? 0,
      isPlaying: current?.isPlaying ?? false,
      autoScrollTranscript: current?.autoScrollTranscript ?? true,
      seekRequest,
    }));
  }

  function handleTranscriptKeyDown(
    event: KeyboardEvent<HTMLElement>,
    offsetSeconds: number | undefined,
    index: number
  ) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    seekToTranscriptOffset(offsetSeconds, index);
  }

  function handleTimestampAdjustmentChange(nextValue: string) {
    const timestampAdjustmentMs = parseTimestampAdjustmentMs(nextValue);
    setTimestampAdjustmentInput(nextValue);
    preference.setValue((current) => ({ ...current, timestampAdjustmentMs }));
  }

  function handleAutoPlayOnClickChange(nextValue: boolean) {
    setAutoPlayOnClick(nextValue);
    preference.setValue((current) => ({ ...current, autoPlayOnClick: nextValue }));
  }

  return (
    <InterviewDetailPanel
      title="Transcript"
      span="3x3"
      panelId={panelId}
      currentSpan={currentSpan}
      className="flex min-h-0 flex-col"
      contentClassName="min-h-0 flex-1"
      contentOverflow="hidden"
      settingsContent={
        <>
          <label className="flex flex-col gap-2 rounded-md border border-neutral-200 px-3 py-2 text-body font-semibold text-neutral-900">
            <span>Timestamp adjustment (ms)</span>
            <input
              type="number"
              inputMode="numeric"
              step="100"
              className="focus-ring no-tile-drag rounded-md border border-neutral-200 px-3 py-2 text-body font-normal tabular-nums text-neutral-900"
              value={timestampAdjustmentInput}
              onChange={(event) => handleTimestampAdjustmentChange(event.target.value)}
            />
          </label>
          <label className="flex items-center justify-between gap-4 rounded-md border border-neutral-200 px-3 py-2 text-body font-semibold text-neutral-900">
            <span>Auto-play on click</span>
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-neutral-300 text-lavender-700"
              checked={autoPlayOnClick}
              onChange={(event) => handleAutoPlayOnClickChange(event.target.checked)}
            />
          </label>
        </>
      }
    >
      {transcript.length === 0 ? (
        <div className="rounded-md border border-dashed border-neutral-200 px-4 py-10 text-center text-body text-neutral-500">
          No transcription available
        </div>
      ) : (
        <div className="h-full min-h-0 space-y-4 overflow-y-auto pr-2">
          {transcript.map((message, index) => {
            const isActive = activeTranscriptIndex === index;
            const adjustedOffsetSeconds = adjustTranscriptOffsetSeconds(
              message.offsetSeconds,
              timestampAdjustmentMs
            );
            const seekTargetSeconds = getTranscriptSeekTargetSeconds(
              message.offsetSeconds,
              index,
              timestampAdjustmentMs
            );
            const canSeek = seekTargetSeconds !== undefined;
            const isParticipant = message.role === 'participant';
            return (
              <div
                key={`${message.timestamp}-${index}`}
                className={[
                  'flex',
                  isParticipant ? 'justify-end pl-10 md:pl-16' : 'justify-start pr-10 md:pr-16',
                ]
                  .filter(Boolean)
                  .join(' ')}
                data-transcript-message-align={isParticipant ? 'right' : 'left'}
              >
                <article
                  ref={isActive ? activeTranscriptRef : undefined}
                  role={canSeek ? 'button' : undefined}
                  tabIndex={canSeek ? 0 : undefined}
                  className={[
                    'w-full max-w-[82%] overflow-hidden rounded-md border transition-colors',
                    canSeek ? 'cursor-pointer focus-ring' : '',
                    canSeek && !isActive ? 'hover:border-lavender-200 hover:bg-lavender-50' : '',
                    isActive
                      ? 'border-yellow-200 bg-yellow-50 shadow-sm'
                      : 'border-neutral-100 bg-neutral-50',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  data-active-transcript={isActive ? 'true' : undefined}
                  data-transcript-role={message.role}
                  onClick={() => seekToTranscriptOffset(message.offsetSeconds, index)}
                  onKeyDown={(event) =>
                    handleTranscriptKeyDown(event, message.offsetSeconds, index)
                  }
                >
                  <div
                    className="group/transcript-metadata relative flex min-h-10 items-center gap-3 px-4 py-3 pr-12 text-small"
                    data-transcript-metadata=""
                  >
                    {canSeek ? (
                      <>
                        <div
                          className="pointer-events-none absolute inset-0 z-10 bg-white opacity-0 transition-opacity group-hover/transcript-metadata:opacity-50 group-focus-within/transcript-metadata:opacity-50"
                          aria-hidden="true"
                          data-transcript-share-overlay=""
                        />
                        <InterviewRecordingTimeShareButton
                          timeSeconds={seekTargetSeconds}
                          location={shareLocation}
                          className="absolute right-2 top-1/2 z-20 -translate-y-1/2 opacity-0 transition-opacity group-hover/transcript-metadata:opacity-100 group-focus-within/transcript-metadata:opacity-100"
                        />
                      </>
                    ) : null}
                    <h3
                      className="min-w-0 flex-1 text-left font-semibold capitalize text-neutral-900"
                      data-transcript-speaker-label=""
                    >
                      {formatStatus(message.role)}
                    </h3>
                    <time
                      className="shrink-0 text-right text-neutral-500"
                      dateTime={message.timestamp}
                    >
                      {formatTranscriptTime(message.timestamp, adjustedOffsetSeconds)}
                    </time>
                  </div>
                  <p className="px-4 pb-3 whitespace-pre-wrap text-body leading-6 text-neutral-700">
                    {message.content}
                  </p>
                </article>
              </div>
            );
          })}
        </div>
      )}
    </InterviewDetailPanel>
  );
}
